import React, { createContext, useState, useRef, useContext, useEffect } from 'react';

interface AudioContextType {
    img: string;
    title: string;
    audioSrc: string | string[];
    isPlaying: boolean;
    currentTrackIndex: number;
    progress: number;
    duration: number;
    currentTime: number;
    playAudio: (img: string, title: string, audioSrc: string | string[], trackIndex?: number) => void;
    pauseAudio: () => void;
    togglePlayPause: () => void;
    playNextTrack: () => void;
    playPreviousTrack: () => void;
    handleScrub: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const AudioContext = createContext<AudioContextType | undefined>(undefined);

export const AudioProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [img, setImg] = useState<string>('');
    const [title, setTitle] = useState<string>('');
    const [audioSrc, setAudioSrc] = useState<string | string[]>('');
    const [isPlaying, setIsPlaying] = useState<boolean>(false);
    const [currentTrackIndex, setCurrentTrackIndex] = useState<number>(0);
    const [progress, setProgress] = useState<number>(0);
    const [duration, setDuration] = useState<number>(0);
    const [currentTime, setCurrentTime] = useState<number>(0);

    const audioRef = useRef<HTMLAudioElement | null>(null);

    // Play audio
    const playAudio = (img: string, title: string, audioSrc: string | string[], trackIndex: number = 0) => {
        setImg(img);
        setTitle(title);
        setAudioSrc(audioSrc);
        setCurrentTrackIndex(trackIndex);
        setIsPlaying(true);

        if (audioRef.current) {
            audioRef.current.src = Array.isArray(audioSrc) ? audioSrc[trackIndex] : audioSrc;
            audioRef.current.play();
        }
    };

    // Pause audio
    const pauseAudio = () => {
        setIsPlaying(false);
        if (audioRef.current) {
            audioRef.current.pause();
        }
    };

    // Toggle play/pause
    const togglePlayPause = () => {
        if (isPlaying) {
            pauseAudio();
        } else {
            playAudio(img, title, audioSrc, currentTrackIndex);
        }
    };

    // Play next track
    const playNextTrack = () => {
        if (Array.isArray(audioSrc) && currentTrackIndex < audioSrc.length - 1) {
            setCurrentTrackIndex((prevIndex) => prevIndex + 1);
            playAudio(img, title, audioSrc, currentTrackIndex + 1);
        }
    };

    // Play previous track
    const playPreviousTrack = () => {
        if (Array.isArray(audioSrc) && currentTrackIndex > 0) {
            setCurrentTrackIndex((prevIndex) => prevIndex - 1);
            playAudio(img, title, audioSrc, currentTrackIndex - 1);
        }
    };

    // Handle scrubbing
    const handleScrub = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (audioRef.current) {
            const scrubTime = (e.target.valueAsNumber / 100) * duration;
            audioRef.current.currentTime = scrubTime;
            setProgress(e.target.valueAsNumber);
        }
    };

    // Update progress and time
    useEffect(() => {
        const audio = audioRef.current;
        if (audio) {
            const updateProgress = () => {
                setCurrentTime(audio.currentTime);
                setProgress((audio.currentTime / audio.duration) * 100 || 0);
            };

            audio.addEventListener('timeupdate', updateProgress);
            audio.addEventListener('loadedmetadata', () => {
                setDuration(audio.duration);
            });

            return () => {
                audio.removeEventListener('timeupdate', updateProgress);
            };
        }
    }, []);

    return (
        <AudioContext.Provider
            value={{
                img,
                title,
                audioSrc,
                isPlaying,
                currentTrackIndex,
                progress,
                duration,
                currentTime,
                playAudio,
                pauseAudio,
                togglePlayPause,
                playNextTrack,
                playPreviousTrack,
                handleScrub,
            }}
        >
            {children}
            <audio ref={audioRef} preload="auto" />
        </AudioContext.Provider>
    );
};

// Custom hook to use the AudioContext
export const useAudio = () => {
    const context = useContext(AudioContext);
    if (!context) {
        throw new Error('useAudio must be used within an AudioProvider');
    }
    return context;
};